import React, { useMemo } from 'react';
import { useQuery } from '@apollo/client';

import { Button } from '@trqk/components';
import { constants, formatMailtoContent, locale } from '@trqk/utils';
import { queryStrings } from '@trqk/resolvers';

import { TopPlanFeatures } from '.';

interface PublisherEditionPlanProps {
    width: number;
}

const { GET_SESSION_CURRENT_USER } = queryStrings;

const planDisplayName =
    locale.enUS.ACCOUNT.SECTIONS.PAYMENT_PLAN.PLAN_DISPLAY_NAMES
        .CHARGEBEE_PLAN_PUBLISHER;

const planLocale =
    locale.enUS.ACCOUNT.SECTIONS.PAYMENT_PLAN.PUBLISHER_EDITION_PLAN;

/**
 * Displays information about the Publisher plan. This plan is built on customized billing, so this
 * component includes a mailto link for the user to email us about setting up a payment plan.
 * @param props - {@link PublisherEditionPlanProps}
 * @returns JSX
 */
export const PublisherEditionPlan = ({
    width,
}: PublisherEditionPlanProps): JSX.Element => {
    const currentUserQuery = useQuery(GET_SESSION_CURRENT_USER);
    const userId = currentUserQuery?.data?.session?.currentUser?.id;

    const bodyLines = [
        planLocale.SET_UP_PLAN_EMAIL_CONTENT.GREETING,
        `${planLocale.SET_UP_PLAN_EMAIL_CONTENT.BODY_PART_1} ${userId}`,
        planLocale.SET_UP_PLAN_EMAIL_CONTENT.BODY_PART_2,
        planLocale.SET_UP_PLAN_EMAIL_CONTENT.SIGN_OFF,
    ];

    const mailtoContent = useMemo(
        () =>
            formatMailtoContent({
                email: constants.SUBSCRIPTIONS_PUBLISHERS_EMAIL_ADDRESS,
                subject: planLocale.SET_UP_PLAN_EMAIL_CONTENT.SUBJECT,
                bodyLines,
            }),
        [userId],
    );

    return (
        <div className="border border-tertiary-200 rounded" style={{ width }}>
            <div className="flex flex-col justify-end border-b border-tertiary-200 px-6">
                <h3 className="font-normal mt-6 pb-4 text-xl">
                    {planDisplayName.toUpperCase()}
                </h3>
                <p className="text-secondary-500 italic text-sm mb-4">
                    {planLocale.DESCRIPTION}
                </p>
            </div>
            <div className="p-6 space-y-4">
                <div className="flex justify-center py-2">
                    <a href={mailtoContent}>
                        <Button autoWidth={true} color="primary-light-bg">
                            <p className="px-10 text-sm font-medium">
                                {planLocale.CONTACT_US}
                            </p>
                        </Button>
                    </a>
                </div>
                <TopPlanFeatures
                    list={
                        locale.enUS.ACCOUNT.SECTIONS.PAYMENT_PLAN.FEATURE_LISTS
                            .PUBLISHER_EDITION
                    }
                    title={`${
                        locale.enUS.ACCOUNT.SECTIONS.PAYMENT_PLAN.CHOOSE_PLAN
                            .TOP
                    } ${planDisplayName.toUpperCase()} ${
                        locale.enUS.ACCOUNT.SECTIONS.PAYMENT_PLAN.CHOOSE_PLAN
                            .FEATURES
                    }`}
                />
                <p className="text-xs text-tertiary-400 italic">
                    *
                    {
                        locale.enUS.MANAGE_GAP_ANALYSIS.STEPS.UPLOAD_DETECTIONS
                            .DESCRIPTION.CURRENTLY_SUPPORTED_FORMATS
                    }
                </p>
            </div>
        </div>
    );
};
